import stylesheet from './AccordionItem.scss?inline';

import CustomComponent from '../../base/customComponent/CustomComponent';
import {AbstractConstructor, withLayer} from '../../base/layer/withLayer';

const SIZE_BREAKPOINTS = ['md', 'lg', 'xl', 'xxl'];

export class NXTAccordionItem extends withLayer(CustomComponent as AbstractConstructor<CustomComponent>) {
    static observedAttributes = ['selected', 'surface', 'size', 'size-md', 'size-lg', 'size-xl', 'size-xxl'];

    private titleElement: HTMLElement;

    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    get isSelected() {
        return this.hasAttribute('selected');
    }

    get surface(): string | null {
        return this.getAttribute('surface');
    }

    get size(): string {
        if (this.hasAttribute('size')) {
            return this.getAttribute('size');
        }
        return this.surface ? 'md' : 'xl';
    }

    getSizeClasses(): string[] {
        const classes = [`accordion-item-${this.size}`];
        for (const breakpoint of SIZE_BREAKPOINTS) {
            const attribute = `size-${breakpoint}`;
            if (this.hasAttribute(attribute)) {
                classes.push(`accordion-item-${this.getAttribute(attribute)}-${breakpoint}-up`);
            }
        }
        return classes;
    }

    render() {
        const itemContainer = document.createElement('div');
        itemContainer.classList.add('accordion-item', ...this.getSizeClasses());
        if (this.surface) {
            itemContainer.classList.add(`accordion-item-${this.surface}`);
        }
        if (this.isSelected) {
            itemContainer.classList.add('selected', 'initial');
        }
        itemContainer.innerHTML = `
            <div class="title" role="button" tabindex="0" aria-expanded="${this.isSelected}">
                <slot name="title"></slot>
            </div>
            <div class="collapsable">
                <div class="content"><slot></slot></div>
            </div>
        `;
        this.titleElement = itemContainer.querySelector('.title');
        this.titleElement.addEventListener('click', () => this.toggle());
        this.titleElement.addEventListener('keydown', (ev: KeyboardEvent) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                this.toggle();
            }
        });
        return itemContainer;
    }

    toggle() {
        if (this.isSelected) {
            this.removeAttribute('selected');
        } else {
            this.setAttribute('selected', '');
        }
    }

    // noinspection JSUnusedGlobalSymbols
    // @ts-ignore
    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'selected') {
            if (oldValue !== newValue) {
                this.updateStyles();
            }
        } else {
            this.updateSizeClasses();
        }
    }

    updateSizeClasses() {
        const itemContainer = this.shadowRoot?.querySelector('.accordion-item');
        if (!itemContainer) {
            return;
        }
        [...itemContainer.classList]
            .filter((cls) => cls !== 'accordion-item' && cls.startsWith('accordion-item-'))
            .forEach((cls) => itemContainer.classList.remove(cls));
        if (this.surface) {
            itemContainer.classList.add(`accordion-item-${this.surface}`);
        }
        itemContainer.classList.add(...this.getSizeClasses());
    }

    updateStyles() {
        const itemContainer = this.shadowRoot.querySelector('.accordion-item');
        if (!itemContainer) {
            return;
        }
        this.titleElement?.setAttribute('aria-expanded', String(this.isSelected));
        if (this.isSelected) {
            this.dispatchEvent(new CustomEvent('accordion-item-open', {
                bubbles: true,
            }));
            itemContainer.classList.add('selected');
            this.expand();
        } else {
            this.dispatchEvent(new CustomEvent('accordion-item-close', {
                bubbles: true,
            }));
            this.collapse();
            itemContainer.classList.add('animated');
            itemContainer.classList.remove('selected', 'initial');
        }
    }

    private expand() {
        const collapsable: HTMLElement = this.shadowRoot.querySelector('.collapsable');
        if(!collapsable) {
            return;
        }
        collapsable.style.maxHeight = `${collapsable.scrollHeight}px`;
        setTimeout(() => {
            if (this.isSelected) {
                collapsable.style.maxHeight = 'unset';
            }
        }, 250);
    }

    private collapse() {
        const collapsable: HTMLElement = this.shadowRoot.querySelector('.collapsable');
        if(!collapsable) {
            return;
        }
        collapsable.style.maxHeight = `${collapsable.scrollHeight}px`;
        setTimeout(() => {
            if (!this.isSelected) {
                collapsable.style.maxHeight = null;
            }
        }, 0);
    }

}

customElements.define('nxt-accordion-item', NXTAccordionItem);
